/**
 * Favorite API Service
 * Kết nối với backend để quản lý yêu thích
 */

import axios from 'axios';
import { API_BASE_URL } from '../config/api';

export interface Favorite {
  id: number;
  user_id: number;
  property_id: number;
  created_at: string;
}

export interface ApiResponse<T> {
  success: boolean;
  data: T;
  message?: string;
  total?: number;
}

class FavoriteApiService {
  private baseURL: string;

  constructor(baseURL: string = API_BASE_URL) {
    this.baseURL = baseURL;
  }

  /**
   * Thêm sản phẩm vào danh sách yêu thích
   */
  async addFavorite(propertyId: number): Promise<ApiResponse<Favorite>> {
    try {
      console.log(`❤️ Adding property ${propertyId} to favorites`);
      
      const response = await axios.post<ApiResponse<Favorite>>(
        `${this.baseURL}/favorites`,
        { property_id: propertyId },
        {
          timeout: 10000,
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      console.log(`✅ Added to favorites successfully`);
      return response.data;
    } catch (error: any) {
      console.error(`❌ Error adding to favorites:`, error);
      return {
        success: false,
        data: {} as Favorite,
        message: error.response?.data?.detail || 'Không thể thêm vào yêu thích'
      };
    }
  }

  /**
   * Xóa sản phẩm khỏi danh sách yêu thích
   */
  async removeFavorite(propertyId: number): Promise<ApiResponse<boolean>> {
    try{
      console.log(`💔 Removing property ${propertyId} from favorites`);
      
      const response = await axios.delete(
        `${this.baseURL}/favorites/${propertyId}`,
        {
          timeout: 10000,
        }
      );

      console.log(`✅ Removed from favorites successfully`);
      return {
        success: true,
        data: true,
        message: response.data.message
      };
    } catch (error: any) {
      console.error(`❌ Error removing from favorites:`, error);
      return {
        success: false,
        data: false,
        message: error.response?.data?.detail || 'Không thể xóa khỏi yêu thích'
      };
    }
  }

  /**
   * Lấy danh sách yêu thích của user
   */
  async getFavorites(): Promise<ApiResponse<Favorite[]>> {
    try {
      console.log(`📋 Fetching favorites list`);
      
      const response = await axios.get<ApiResponse<Favorite[]>>(
        `${this.baseURL}/favorites`,
        {
          timeout: 10000,
        }
      );

      console.log(`✅ Loaded ${response.data.data?.length || 0} favorites`);
      return response.data;
    } catch (error: any) {
      console.error(`❌ Error fetching favorites:`, error);
      return {
        success: false,
        data: [],
        message: 'Không thể tải danh sách yêu thích'
      };
    }
  }

  /**
   * Lấy danh sách property yêu thích với thông tin đầy đủ
   */
  async getFavoriteProperties(): Promise<ApiResponse<any[]>> {
    try {
      console.log(`📋 Fetching favorite properties`);
      
      const response = await axios.get(
        `${this.baseURL}/favorites/properties`,
        {
          timeout: 10000,
        }
      );

      console.log(`✅ Loaded ${response.data.data?.length || 0} favorite properties`);
      return response.data;
    } catch (error: any) {
      console.error(`❌ Error fetching favorite properties:`, error);
      return {
        success: false,
        data: [],
        message: 'Không thể tải danh sách sản phẩm yêu thích'
      };
    }
  }

  /**
   * Kiểm tra xem property có được yêu thích không
   */
  async checkFavorite(propertyId: number): Promise<boolean> {
    try {
      const response = await axios.get(
        `${this.baseURL}/favorites/check/${propertyId}`,
        {
          timeout: 5000,
        }
      );

      return response.data.data?.is_favorited || false;
    } catch (error: any) {
      console.error(`❌ Error checking favorite:`, error);
      return false;
    }
  }
}

export const favoriteApi = new FavoriteApiService();

